import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarDays, User, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

import BookingCalendar from "./booking/BookingCalendar";
import BookingForm from "./booking/BookingForm";
import BookingSuccess from "./booking/BookingSuccess";
import { useAvailability, useApiCache } from "@/hooks/useBookingApi";


/**
 * Hauptkomponente für den Buchungsbereich
 * Zeigt Kalender und Buchungsformular nebeneinander an
 * Ermöglicht eine bessere Übersicht über den gesamten Buchungsprozess
 * 
 * @param {Object} props - Komponenten-Props
 * @param {Function} props.onBookingSuccess - Callback für erfolgreiche Buchungen
 * 
 * @example
 * ```jsx
 * <BookingSection onBookingSuccess={() => console.log('Buchung erfolgreich!')} />
 * ```
 */
export default function BookingSection({ onBookingSuccess }) {
  // State für den ausgewählten Datumsbereich
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  
  // State für die Buchungsdaten nach erfolgreicher Eingabe
  const [bookingData, setBookingData] = useState(null);
  
  // State für die Anzeige der Erfolgsseite
  const [showSuccess, setShowSuccess] = useState(false);
  
  // State für den aktuellen Monat im Kalender
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Verwende den neuen API-Hook für Verfügbarkeitsdaten
  const { data: availabilityData, loading: availabilityLoading, error: availabilityError, refetch, fetchForMonth } = useAvailability(currentMonth);
  const { clearCache } = useApiCache();

  /**
   * Behandelt die Auswahl eines Datumsbereichs
   * Aktualisiert den ausgewählten Zeitraum für das Formular
   * 
   * @param {Object} dateRange - Ausgewählter Datumsbereich mit start und end Properties
   * @param {Date} dateRange.start - Startdatum der Buchung
   * @param {Date} dateRange.end - Enddatum der Buchung
   */
  const handleDateRangeSelect = (dateRange) => {
    setSelectedDateRange(dateRange);
  };

  /**
   * Behandelt den Abschluss einer Buchung
   * Speichert die API-Response und Formulardaten und zeigt die Erfolgsseite an
   * 
   * @param {Object} bookingResponse - API-Response der Buchungsanfrage
   * @param {Object} originalFormData - Originale Formulardaten für Anzeige
   */
  const handleBookingComplete = (bookingResponse, originalFormData) => {
    setBookingData({ bookingResponse, originalFormData });
    setShowSuccess(true);
    onBookingSuccess();
  };

  /**
   * Behandelt Änderungen des angezeigten Monats im Kalender
   * Lädt neue Verfügbarkeitsdaten für den neuen Monat
   * @param {Date} newMonth - Der neue Monat
   */
  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
    fetchForMonth(newMonth);
  };

  /**
   * Startet eine neue Buchung
   * Setzt alle States zurück und kehrt zum Kalender zurück
   * Aktualisiert auch die Verfügbarkeitsdaten
   * 
   * Diese Funktion wird von der Erfolgsseite aufgerufen und
   * ermöglicht es dem Benutzer, weitere Buchungen vorzunehmen
   */
  const handleNewBooking = () => {
    setShowSuccess(false);
    setSelectedDateRange(null);
    setBookingData(null);
    // Lösche Cache und lade Verfügbarkeitsdaten neu nach erfolgreicher Buchung
    clearCache();
    refetch();
  };

  // Zeige Erfolgsseite wenn Buchung abgeschlossen
  if (showSuccess && bookingData) {
    return (
      <BookingSuccess 
        bookingResponse={bookingData.bookingResponse}
        originalFormData={bookingData.originalFormData}
        onNewBooking={handleNewBooking}
      />
    );
  }

  return (
    <section id="booking-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Überschrift und Beschreibung der Buchungssektion */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: 'var(--primary)' }}>
            Jetzt <span className="font-semibold">buchen</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Prüfen Sie die Verfügbarkeit und stellen Sie Ihre unverbindliche Buchungsanfrage.
          </p>
        </motion.div>

        {/* Hauptbereich mit zwei Containern nebeneinander */}
        <motion.div
          className="grid lg:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Linker Container: Kalender (2/3) */}
          <div className="lg:col-span-2">
            <Card className="border-none shadow-2xl h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-semibold" style={{ color: 'var(--primary)' }}>
                    <CalendarDays className="inline-block w-6 h-6 mr-2" />
                    Verfügbarkeitskalender
                  </CardTitle>

                </div>
                {availabilityError && (
                  <div className="text-red-600 text-sm mt-2">
                    Fehler beim Laden der Verfügbarkeitsdaten: {availabilityError}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <BookingCalendar 
                  onDateRangeSelect={handleDateRangeSelect}
                  selectedDateRange={selectedDateRange}
                  availabilityData={availabilityData}
                  loading={availabilityLoading}
                  onMonthChange={handleMonthChange}
                />
              </CardContent>
            </Card>
          </div>

          {/* Rechter Container: Buchungsformular (1/3) */}
          <div className="lg:col-span-1">
            <Card className="border-none shadow-2xl h-full sticky top-8">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold" style={{ color: 'var(--primary)' }}>
                  <User className="inline-block w-6 h-6 mr-2" />
                  Buchungsanfrage
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDateRange ? (
                  <BookingForm 
                    selectedDateRange={selectedDateRange}
                    onBookingComplete={handleBookingComplete}
                    availabilityData={availabilityData}
                    compact={true}
                  />
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <CalendarDays className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">Zeitraum auswählen</p>
                    <p className="text-sm">
                      Wählen Sie im Kalender links einen Zeitraum aus, um mit der Buchung zu beginnen.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>
  );
}